/** Drag a panel inside a bounds element; persists offset in localStorage. */

export interface DragOpts {
  handle: HTMLElement;
  panel: HTMLElement;
  bounds: HTMLElement;
  storageKey: string;
  /** Fired on pointer-up when the pointer barely moved (tap to open, etc.). */
  onTap?: () => void;
}

/** Movement below this (px) counts as a tap, not a drag. */
const TAP_SLOP_PX = 14;

export function enableDrag({ handle, panel, bounds, storageKey, onTap }: DragOpts): () => void {
  handle.classList.add("ui-drag-handle");
  panel.classList.add("ui-draggable");

  const saved = loadPos(storageKey);
  if (saved) {
    applyPos(panel, bounds, saved.x, saved.y);
  }

  let dragging = false;
  let startX = 0;
  let startY = 0;
  let originLeft = 0;
  let originTop = 0;
  let pointerId = -1;
  let moved = false;

  const onDown = (event: PointerEvent) => {
    if (event.button !== 0) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target?.closest("button, a, input, select, textarea, .inventory-slot, .menu-row")) {
      return;
    }
    dragging = true;
    moved = false;
    pointerId = event.pointerId;
    handle.setPointerCapture(pointerId);
    const rect = panel.getBoundingClientRect();
    const parent = bounds.getBoundingClientRect();
    startX = event.clientX;
    startY = event.clientY;
    originLeft = rect.left - parent.left;
    originTop = rect.top - parent.top;
    // Don't preventDefault yet - allows a clean click/tap; we suppress once dragging.
  };

  const onMove = (event: PointerEvent) => {
    if (!dragging || event.pointerId !== pointerId) {
      return;
    }
    const dx = event.clientX - startX;
    const dy = event.clientY - startY;
    if (!moved) {
      if (Math.hypot(dx, dy) <= TAP_SLOP_PX) {
        return;
      }
      moved = true;
      panel.classList.add("is-dragging");
      event.preventDefault();
    }
    applyPos(panel, bounds, originLeft + dx, originTop + dy);
  };

  const onUp = (event: PointerEvent) => {
    if (!dragging || event.pointerId !== pointerId) {
      return;
    }
    dragging = false;
    panel.classList.remove("is-dragging");
    try {
      handle.releasePointerCapture(pointerId);
    } catch {
      /* already released */
    }
    if (moved) {
      const rect = panel.getBoundingClientRect();
      const parent = bounds.getBoundingClientRect();
      savePos(storageKey, rect.left - parent.left, rect.top - parent.top);
    } else if (onTap) {
      event.preventDefault();
      onTap();
    }
  };

  handle.addEventListener("pointerdown", onDown);
  handle.addEventListener("pointermove", onMove);
  handle.addEventListener("pointerup", onUp);
  handle.addEventListener("pointercancel", onUp);

  const onResize = () => {
    const rect = panel.getBoundingClientRect();
    const parent = bounds.getBoundingClientRect();
    applyPos(panel, bounds, rect.left - parent.left, rect.top - parent.top);
  };
  window.addEventListener("resize", onResize);

  return () => {
    handle.removeEventListener("pointerdown", onDown);
    handle.removeEventListener("pointermove", onMove);
    handle.removeEventListener("pointerup", onUp);
    handle.removeEventListener("pointercancel", onUp);
    window.removeEventListener("resize", onResize);
  };
}

function applyPos(panel: HTMLElement, bounds: HTMLElement, x: number, y: number): void {
  const maxX = Math.max(0, bounds.clientWidth - panel.offsetWidth);
  const maxY = Math.max(0, bounds.clientHeight - panel.offsetHeight);
  const nx = clamp(x, 0, maxX);
  const ny = clamp(y, 0, maxY);
  panel.style.left = `${nx}px`;
  panel.style.top = `${ny}px`;
  panel.style.right = "auto";
  panel.style.bottom = "auto";
  panel.style.transform = "none";
}

function loadPos(key: string): { x: number; y: number } | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as { x?: number; y?: number };
    if (typeof parsed.x === "number" && typeof parsed.y === "number") {
      return { x: parsed.x, y: parsed.y };
    }
  } catch {
    /* ignore */
  }
  return null;
}

function savePos(key: string, x: number, y: number): void {
  try {
    localStorage.setItem(key, JSON.stringify({ x, y }));
  } catch {
    /* ignore */
  }
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}
